import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowForward,
  ArrowOutward,
  Assessment,
  AutoStories,
  CheckCircle,
  EmojiEvents,
  FactCheck,
  Groups,
  LibraryBooks,
  MenuBook,
  School,
  Timeline,
  TrackChanges,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import products from './ProductData';
import skylarkReadsLogo from '../assets/skylark-reads-logo.jpg';

const product = products[0];

const cycleSteps = [
  {
    title: 'Assess',
    icon: <Assessment />,
    text: 'Students begin with a diagnostic reading assessment that evaluates their reading ability across multiple skills.',
  },
  {
    title: 'Recommend',
    icon: <TrackChanges />,
    text: 'Skylark Reads determines an appropriate reading level and recommends a suitable reading range.',
  },
  {
    title: 'Read',
    icon: <MenuBook />,
    text: 'Students choose from matched physical books and continue the actual reading experience away from the screen.',
  },
  {
    title: 'Quiz',
    icon: <FactCheck />,
    text: 'After finishing a book, students complete a short multiple-choice comprehension quiz.',
  },
  {
    title: 'Track',
    icon: <Timeline />,
    text: 'Books completed, quiz performance, reading targets, milestones, badges, and level progress are continuously tracked.',
  },
  {
    title: 'Progress',
    icon: <EmojiEvents />,
    text: 'Periodic reassessments update the student’s level and recommendations so the journey keeps moving at the right pace.',
  },
];

const dashboards = [
  {
    title: 'Students',
    icon: <AutoStories />,
    text: 'A clear reading path with personalized books, goals, milestones, badges, and visible progress.',
  },
  {
    title: 'Teachers',
    icon: <School />,
    text: 'Class-level visibility into reading activity, assessment performance, quiz results, and growth over time.',
  },
  {
    title: 'Parents',
    icon: <Groups />,
    text: "A simple way to understand a child's reading level, completed books, comprehension, and development.",
  },
  {
    title: 'Schools',
    icon: <LibraryBooks />,
    text: 'Centralized student, teacher, assessment, progress, and library management for a structured reading program.',
  },
];

const metrics = ['Books completed', 'Quiz performance', 'Reading targets', 'Reading milestones', 'Badges and achievements', 'Reading level progress'];

const SkylarkReads = () => {
  return (
    <Box>
      <Box
        component="section"
        sx={{
          py: { xs: 8, md: 12 },
          background:
            'radial-gradient(circle at 82% 18%, rgba(25, 167, 206, 0.22), transparent 28%), linear-gradient(135deg, #f7fbff 0%, #edf8fb 52%, #ffffff 100%)',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={skylarkReadsLogo}
                alt="Skylark Reads logo"
                sx={{
                  width: { xs: 180, sm: 220 },
                  height: 'auto',
                  display: 'block',
                  mb: 3,
                  borderRadius: 3,
                  boxShadow: '0 18px 45px rgba(16, 32, 51, 0.1)',
                }}
              />
              <Typography variant="h2" component="h1" sx={{ fontSize: { xs: '2.7rem', md: '4.8rem' }, lineHeight: 1.02 }}>
                {product.name}
              </Typography>
              <Typography variant="h4" component="p" sx={{ color: 'primary.main', mt: 3, lineHeight: 1.25 }}>
                {product.tagline}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mt: 3, fontSize: '1.08rem', lineHeight: 1.85 }}>
                {product.description}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mt: 2, fontSize: '1.02rem', lineHeight: 1.8 }}>
                The platform helps schools, teachers, parents, and students understand a child's current reading level and provides a structured path to improve it over time.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }}>
                <Button component="a" href={product.externalUrl} target="_blank" rel="noreferrer" variant="contained" size="large" endIcon={<ArrowOutward />}>
                  Visit Skylark Reads
                </Button>
                <Button component={Link} to="/contact" variant="outlined" size="large" endIcon={<ArrowForward />}>
                  Talk to Us
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  background: '#0b1727',
                  color: 'white',
                  borderRadius: 4,
                  boxShadow: '0 34px 90px rgba(15, 47, 95, 0.2)',
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                  <Stack spacing={3}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        component="img"
                        src={skylarkReadsLogo}
                        alt=""
                        aria-hidden="true"
                        sx={{
                          width: 88,
                          height: 88,
                          objectFit: 'contain',
                          borderRadius: 2,
                          backgroundColor: 'white',
                          p: 1,
                        }}
                      />
                      <Box>
                        <Typography variant="h5">A reading journey that stays physical.</Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
                          Technology guides the program. Students still read real books.
                        </Typography>
                      </Box>
                    </Stack>
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.14)' }} />
                    {['Personalized assessment', 'Matched physical books', 'Short comprehension quizzes', 'Continuous progress tracking'].map((item) => (
                      <Stack direction="row" spacing={2} alignItems="center" key={item}>
                        <CheckCircle sx={{ color: 'secondary.main' }} />
                        <Typography variant="h6">{item}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 7, md: 10 }, backgroundColor: '#ffffff' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="h3">How Skylark Reads Works</Typography>
            <Typography sx={{ color: 'text.secondary', mt: 2, maxWidth: 760, mx: 'auto', lineHeight: 1.8 }}>
              Assess → Recommend → Read → Quiz → Track → Progress. A continuous cycle that helps each student move forward at an appropriate pace.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {cycleSteps.map((step, index) => (
              <Grid item xs={12} sm={6} md={4} key={step.title}>
                <Card sx={{ height: '100%', border: '1px solid rgba(15,47,95,0.08)', boxShadow: '0 18px 45px rgba(16,32,51,0.06)' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ color: 'secondary.main', display: 'flex' }}>{step.icon}</Box>
                      <Typography variant="h6">0{index + 1}</Typography>
                    </Stack>
                    <Typography variant="h5" sx={{ mt: 3 }}>
                      {step.title}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', mt: 1.5, lineHeight: 1.75 }}>{step.text}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 7, md: 10 }, background: 'linear-gradient(135deg, #0b1727 0%, #0f2f5f 100%)', color: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="h3">More than a digital reading platform</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.76)', mt: 2, lineHeight: 1.85 }}>
                Skylark Reads combines technology with physical reading. Instead of replacing books with screens, technology is used to assess ability, match students with suitable books, manage the program, measure comprehension, and provide useful information to teachers and parents.
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                {metrics.map((item) => (
                  <Grid item xs={12} sm={6} key={item}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 2, border: '1px solid rgba(255,255,255,0.14)', borderRadius: 2, background: 'rgba(255,255,255,0.06)' }}>
                      <CheckCircle sx={{ color: 'secondary.main' }} />
                      <Typography>{item}</Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 7, md: 10 }, backgroundColor: '#f5f8fc' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="h3">Built for schools, students, and parents</Typography>
            <Typography sx={{ color: 'text.secondary', mt: 2, maxWidth: 720, mx: 'auto', lineHeight: 1.8 }}>
              Everyone gets the right level of visibility, from student motivation to school-wide reading program management.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {dashboards.map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item.title}>
                <Card sx={{ height: '100%', border: '1px solid rgba(15,47,95,0.08)', boxShadow: 'none' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ color: 'secondary.main', display: 'flex', mb: 2 }}>{item.icon}</Box>
                    <Typography variant="h5">{item.title}</Typography>
                    <Typography sx={{ color: 'text.secondary', mt: 1.5, lineHeight: 1.7 }}>{item.text}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 7, md: 10 }, backgroundColor: '#ffffff' }}>
        <Container maxWidth="lg">
          <Card sx={{ borderRadius: 4, border: '1px solid rgba(15,47,95,0.08)', boxShadow: '0 24px 70px rgba(16,32,51,0.08)' }}>
            <CardContent sx={{ p: { xs: 3, md: 6 } }}>
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={7}>
                  <Typography variant="h3">The Skylark Reads Approach</Typography>
                  <Typography sx={{ color: 'text.secondary', mt: 2, lineHeight: 1.8 }}>
                    Skylark Reads creates a measurable reading cycle that makes progress visible to students, parents, teachers, and schools while keeping the actual reading experience rooted in physical books.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Button component="a" href={product.externalUrl} target="_blank" rel="noreferrer" variant="contained" size="large" fullWidth endIcon={<ArrowOutward />}>
                    Visit www.skylarkreads.com
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default SkylarkReads;
